import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useState } from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import JsonLd from "@/components/JsonLd";
import { howToLd } from "@/lib/structuredData";

/**
 * Free tool: honest automation cost estimator. All math client-side and
 * documented on the page. Competitor numbers are published July-2026
 * prices with clearly labeled estimation assumptions — estimates, not
 * quotes. Sliders sync to the URL so results are shareable.
 */

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

/** ActiveCampaign Pro (annual) — published band pricing, July 2026. */
function acEstimate(contacts: number): number {
  if (contacts <= 1_000) return 79;
  if (contacts <= 2_500) return 145;
  if (contacts <= 5_000) return 209;
  if (contacts <= 10_000) return 289;
  if (contacts <= 25_000) return 489;
  return 689;
}

/** GHL: $297 Unlimited + typical usage fees that scale with sending. */
function ghlEstimate(emailsPerMonth: number): number {
  const usage = clamp(20 + emailsPerMonth / 1_000, 20, 150);
  return 297 + Math.round(usage);
}

/** Make: ~1.5 ops per email step (trigger+filter overhead), $9/10k pack. */
function makeEstimate(emailsPerMonth: number): number {
  const ops = emailsPerMonth * 1.5;
  const packs = Math.max(1, Math.ceil(ops / 10_000));
  return Math.round(9 + (packs - 1) * 9 + 12); // base plan + extra packs + ~Pro delta
}

/** Frazzle plan by contacts/workspaces (flat; sending at provider cost). */
function frazzlePlan(contacts: number, workspaces: number): { name: string; price: number } {
  if (workspaces > 1) return { name: "Scale", price: 199 };
  if (contacts > 2_500) return { name: "Pro", price: 79 };
  if (contacts > 500) return { name: "Starter", price: 29 };
  return { name: "Free", price: 0 };
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between text-[13px] font-medium text-slate-700">
        {label}
        <span className="text-[14px] font-bold text-indigo-700">{format(value)}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1.5 w-full accent-indigo-600"
      />
    </label>
  );
}

export default function CostCalculatorPage() {
  const router = useRouter();
  const [contacts, setContacts] = useState(2_500);
  const [emails, setEmails] = useState(10_000);
  const [workspaces, setWorkspaces] = useState(1);

  // Shareable results: hydrate from URL once, then mirror state into it.
  useEffect(() => {
    if (!router.isReady) return;
    const c = Number(router.query.c);
    const e = Number(router.query.e);
    const w = Number(router.query.w);
    if (c) setContacts(clamp(c, 500, 50_000));
    if (e) setEmails(clamp(e, 1_000, 200_000));
    if (w) setWorkspaces(clamp(w, 1, 25));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);
  useEffect(() => {
    if (!router.isReady) return;
    const t = setTimeout(() => {
      void router.replace(
        { query: { c: contacts, e: emails, w: workspaces } },
        undefined,
        { shallow: true }
      );
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, emails, workspaces, router.isReady]);

  const rows = useMemo(() => {
    const frazzle = frazzlePlan(contacts, workspaces);
    const sendingCost = Math.round((emails / 1_000) * 0.8); // ~Mailgun/SES retail
    return {
      frazzle,
      sendingCost,
      list: [
        {
          name: `FrazzleCRM ${frazzle.name}`,
          price: frazzle.price + sendingCost,
          detail: `$${frazzle.price} flat + ~$${sendingCost} sending at provider cost (your domain)`,
          us: true,
        },
        {
          name: "ActiveCampaign Pro",
          price: acEstimate(contacts) * workspaces,
          detail:
            workspaces > 1
              ? `contact-band pricing × ${workspaces} accounts (no multi-workspace plan)`
              : "contact-band pricing (annual rate); sending included",
        },
        {
          name: "GoHighLevel Unlimited",
          price: ghlEstimate(emails * workspaces),
          detail: "$297 + typical usage fees for email/SMS/AI at this volume",
        },
        {
          name: "Make (+ your CRM)",
          price: makeEstimate(emails * workspaces),
          detail:
            "credits at ~1.5 ops per email step — CRM not included, add your own",
        },
      ].sort((a, b) => a.price - b.price),
    };
  }, [contacts, emails, workspaces]);

  return (
    <SiteShell
      title="Marketing automation cost calculator (2026) — free tool"
      description="Free calculator: estimate what your funnel actually costs per month on ActiveCampaign, GoHighLevel, Make and FrazzleCRM — contacts, email volume and client workspaces. Published July-2026 pricing, assumptions shown."
    >
      {/* HowTo mirrors the three things the page actually asks you to do. */}
      <JsonLd
        data={howToLd(
          "How to estimate your marketing automation cost",
          "Compare your monthly marketing-automation cost across GoHighLevel, ActiveCampaign, Make and FrazzleCRM using published 2026 pricing.",
          [
            {
              name: "Enter your numbers",
              text: "Slide in your monthly contacts, email volume and SMS with the sliders.",
            },
            {
              name: "Compare live estimates",
              text: "Estimated monthly cost updates live across GoHighLevel, ActiveCampaign, Make and FrazzleCRM's flat price.",
            },
            {
              name: "Share the result",
              text: "The sliders sync to the URL, so you can copy the link to share your estimate.",
            },
          ]
        )}
      />

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-4xl px-6 pb-10 pt-16 text-center">
          <div className="mx-auto mb-4 w-fit rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[12px] font-semibold text-indigo-700">
            Free tool — assumptions shown, no fine print
          </div>
          <h1 className="text-[36px] font-extrabold leading-[1.12] tracking-tight text-slate-900 sm:text-[44px]">
            What does your funnel{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              actually cost?
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-[15.5px] leading-relaxed text-slate-600">
            Slide to your numbers. Estimates update live and the URL is
            shareable.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 pb-4">
        <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <Slider
            label="Contacts"
            value={contacts}
            min={500}
            max={50_000}
            step={500}
            format={(v) => v.toLocaleString()}
            onChange={setContacts}
          />
          <Slider
            label="Automation emails / month"
            value={emails}
            min={1_000}
            max={200_000}
            step={1_000}
            format={(v) => v.toLocaleString()}
            onChange={setEmails}
          />
          <Slider
            label="Client workspaces (agencies)"
            value={workspaces}
            min={1}
            max={25}
            step={1}
            format={(v) => String(v)}
            onChange={setWorkspaces}
          />
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
          {rows.list.map((r, i) => (
            <div
              key={r.name}
              className={`flex items-center gap-4 border-b border-slate-100 px-5 py-4 last:border-0 ${
                r.us ? "bg-indigo-50/50" : "bg-white"
              }`}
            >
              <span className="w-6 text-[13px] font-bold text-slate-400">
                {i + 1}.
              </span>
              <div className="min-w-0 flex-1">
                <div className={`text-[14.5px] font-bold ${r.us ? "text-indigo-800" : "text-slate-800"}`}>
                  {r.name}
                </div>
                <div className="text-[12px] leading-snug text-slate-500">{r.detail}</div>
              </div>
              <div className={`shrink-0 text-[20px] font-extrabold ${r.us ? "text-indigo-700" : "text-slate-700"}`}>
                ${r.price.toLocaleString()}
                <span className="text-[12px] font-medium text-slate-400">/mo</span>
              </div>
            </div>
          ))}
        </div>

        <details className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-[12.5px] leading-relaxed text-slate-500">
          <summary className="cursor-pointer font-semibold text-slate-600">
            Assumptions (read these — estimates, not quotes)
          </summary>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Vendor prices as published July 2026, annual rates where offered. Always confirm current pricing with each vendor.</li>
            <li>ActiveCampaign: Pro tier band pricing by contact count; multiple client workspaces modeled as separate accounts.</li>
            <li>GoHighLevel: $297 Unlimited + usage fees estimated $20–150/mo scaling with send volume.</li>
            <li>Make: ~1.5 operations per email step (trigger/filter overhead), 10k-op packs ≈ $9; CRM cost not included.</li>
            <li>FrazzleCRM: flat plan + sending through your own provider estimated at ~$0.80 per 1,000 emails (Mailgun/SES retail).</li>
          </ul>
        </details>

        <div className="my-8 rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-5 text-center">
          <p className="text-[14px] font-semibold text-slate-800">
            FrazzleCRM {rows.frazzle.name}: ${rows.frazzle.price}/mo flat —
            contacts don&apos;t change the price.
          </p>
          <a
            href={`${APP_URL}/signup`}
            className="mt-3 inline-block rounded-xl bg-indigo-600 px-6 py-2.5 text-[14px] font-semibold text-white hover:bg-indigo-500"
          >
            Start free — no card
          </a>
          <p className="mt-3 text-[12px] text-slate-500">
            Deep dives:{" "}
            <Link href="/vs/gohighlevel" className="font-medium text-indigo-600">vs GoHighLevel</Link>
            {" · "}
            <Link href="/vs/activecampaign" className="font-medium text-indigo-600">vs ActiveCampaign</Link>
            {" · "}
            <Link href="/vs/make" className="font-medium text-indigo-600">vs Make</Link>
          </p>
        </div>
      </section>
    </SiteShell>
  );
}
